import { ProgressCard } from "../common/ProgressCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Info, RefreshCcw, Layers, FileBarChart, FilePen, CloudUpload, Check } from "lucide-react";
import { CardPanelHeader } from "../common/CardPanelHeader";
import { InfoCallout } from "../common/InfoCallout";
import { useEffect, useState } from "react";
import { calculateWasteEmissions, type WasteEmissionsResult } from "@/service/emissions";
import { useWizard } from "@/contexts/WizardContext";
import type { Scope3WasteData } from "@/contexts/wizard/types";
import { DocumentUploadSection } from "../common/DocumentUploadSection";
import { UploadSectionIcon } from "../common/UploadSectionIcon";
import { LockedBanner } from "../common/LockedBanner";
import { cn } from "@/lib/utils";
import { SectionCard } from "../common/SectionCard";
import { SectionHeader } from "../common/SectionHeader";
import { TransitionMeasuresBlock } from "@/components/workflow/common/TransitionMeasuresBlock";

const SCOPE_PANEL_CLASS = "wizard-card border border-slate-100 bg-primary/10";
const TEXTAREA_CLASS = "min-h-[128px] rounded-xl border-slate-300 bg-slate-50 px-3 py-2 text-sm leading-5 text-slate-900 shadow-none placeholder:text-gray-500 focus-visible:ring-slate-300";
const INPUT_CLASS = "h-11 rounded-l-xl border-slate-300 border-r-0 bg-white px-3 text-sm leading-5 text-slate-900 shadow-none placeholder:text-gray-500 focus-visible:ring-slate-300";
const STANDALONE_INPUT_CLASS = "h-11 rounded-xl border-slate-200 bg-slate-50 px-3 text-sm leading-5 text-slate-900 shadow-none placeholder:text-gray-500 focus-visible:ring-slate-300";

function InfoIcon() {
  return <Info className="h-[15px] w-[15px] shrink-0 text-slate-500" strokeWidth={1.8} />;
}

function OptionCard({ id, name, selected, onSelect, disabled, label }: { id: string; name: string; selected: boolean; onSelect: () => void; disabled: boolean; label: string }) {
  return <label htmlFor={id} className={cn("flex min-h-[56px] flex-1 items-center gap-3 rounded-2xl border p-4 transition-colors", disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer", selected ? "border-primary/20 bg-primary/5" : "border-slate-200 bg-white hover:border-slate-300")}><input id={id} type="radio" name={name} checked={selected} onChange={onSelect} disabled={disabled} className="sr-only" /><span className={cn("flex h-4 w-4 shrink-0 items-center justify-center rounded-full border", selected ? "border-slate-500 bg-primary" : "border-gray-500 bg-white")} aria-hidden="true">{selected && <span className="h-2 w-2 rounded-full bg-white" />}</span><span className="text-sm font-medium leading-5 text-slate-900">{label}</span></label>;
}

// VSME B7 circular economy principles. The `value` is what we persist; the `example`
// text is shown as helper context so the user understands each principle.
const CIRCULAR_ECONOMY_PRINCIPLES: { value: string; example: string }[] = [
  {
    value: "Eliminate waste and pollution",
    example: "e.g. improving processes, considerate usability, reusability, repairability, disassembly and remanufacturing when offering a product, etc",
  },
  {
    value: "Circulate products and materials",
    example: "e.g. integrate circularity when designing a product, incorporate biomaterials like biodegradable crop covers instead of plastic, etc",
  },
  {
    value: "Regenerate nature",
    example: "e.g. drainage, habitat provision, thermal regulation, etc",
  },
];

function PrincipleCheckbox({ id, checked, onToggle, disabled, label, example }: { id: string; checked: boolean; onToggle: () => void; disabled: boolean; label: string; example: string }) {
  return (
    <label htmlFor={id} className={cn("flex items-start gap-3 rounded-2xl border p-4 transition-colors", disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer", checked ? "border-primary/20 bg-primary/5" : "border-slate-200 bg-white hover:border-slate-300")}>
      <input id={id} type="checkbox" checked={checked} onChange={onToggle} disabled={disabled} className="sr-only" />
      <span className={cn("mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border", checked ? "border-slate-500 bg-primary" : "border-gray-500 bg-white")} aria-hidden="true">{checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}</span>
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-medium leading-5 text-slate-900">{label}</span>
        <span className="text-sm leading-5 text-slate-500">{example}</span>
      </span>
    </label>
  );
}

function SplitNumberSelect({ inputId, value, placeholder, onValueChange, unit, onUnitChange, disabled }: { inputId: string; value: number | null; placeholder: string; onValueChange: (value: string) => void; unit: "kg" | "tonnes"; onUnitChange: (value: "kg" | "tonnes") => void; disabled: boolean }) {
  return (
    <div className="flex items-stretch">
      <Input 
        id={inputId} 
        type="number" 
        step="0.1" 
        value={value ?? ""} 
        onChange={(e) => onValueChange(e.target.value)} 
        placeholder={placeholder} 
        disabled={disabled} 
        // Combine your existing INPUT_CLASS with the new utility classes
        className={`${INPUT_CLASS} rounded-r-none border-r-0 focus-visible:z-10`} 
      />
      
      <Select value={unit} onValueChange={onUnitChange} disabled={disabled}>
        <SelectTrigger className="h-11 w-[102px] rounded-l-none rounded-r-xl border-slate-300 bg-slate-50 px-3 text-xs text-slate-500 shadow-none focus:ring-slate-300 focus:z-10">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="kg">kg</SelectItem>
          <SelectItem value="tonnes">tonnes</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function Scope3Metric({ label, value }: { label: string; value: string }) {
  return <div className="rounded-xl border border-slate-100 bg-white p-4 text-center"><div className="pb-1 text-xs font-bold uppercase leading-4 tracking-wide text-slate-400">{label}</div><div className="flex items-end justify-center gap-1"><span className="text-2xl font-bold leading-8 text-slate-500">{value}</span><span className="pb-1 text-xs font-medium leading-4 text-slate-500">tCO2e</span></div></div>;
}

function CircularIcon() {
  return <RefreshCcw className="h-6 w-6 shrink-0 text-primary" strokeWidth={2} />;
}

function TotalsIcon() {
  return <Layers className="h-6 w-6 shrink-0 text-primary" strokeWidth={2} />;
}

function MaterialsIcon() {
  return <FileBarChart className="h-6 w-6 shrink-0 text-primary" strokeWidth={2} />;
}

function NotesIcon() {
  return <FilePen className="h-6 w-6 shrink-0 text-primary" strokeWidth={2} />;
}


function UploadCloudIcon() {
  return <CloudUpload className="h-8 w-8 shrink-0 text-primary" strokeWidth={1.5} />;
}

export function WasteCard({ onClose }: { onClose: () => void }) {
  const {
    wasteData,
    updateWasteData,
    wasteCompletion,
    forceSave,
    saveStatus,
    wasteSupportingDocuments,
    setWasteSupportingDocuments,
    isLocked,
    isReadOnly,
  } = useWizard();
  const disabled = isLocked || isReadOnly;
  const defaultRecyclingByMaterial = { paper: null, plastic: null, glass: null, metal: null, organic: null, other: null } as const;
  const [wasteBreakdown, setWasteBreakdown] = useState<WasteEmissionsResult["breakdown"] | null>(null);

  const handleNumberChange = (field: keyof typeof wasteData, value: string) => {
    const numValue = value.trim() === "" ? null : parseFloat(value);
    updateWasteData({ [field]: isNaN(numValue as number) ? null : numValue });
  };

  const buildScope3WasteData = (base?: Scope3WasteData): Scope3WasteData => ({
    recyclingByMaterial: base?.recyclingByMaterial || { ...defaultRecyclingByMaterial },
    recyclingUnit: base?.recyclingUnit ?? "tonnes",
    generalWasteToLandfill: base?.generalWasteToLandfill ?? null,
    generalWasteUnit: base?.generalWasteUnit ?? "tonnes",
    hazardousWasteDisposal: base?.hazardousWasteDisposal ?? null,
    hazardousWasteUnit: base?.hazardousWasteUnit ?? "tonnes",
    scope3WasteTco2e: base?.scope3WasteTco2e ?? null,
  });

  const updateScope3WasteData = (
    updates: Omit<Partial<Scope3WasteData>, "recyclingByMaterial"> & { recyclingByMaterial?: Partial<Scope3WasteData["recyclingByMaterial"]> }
  ) => {
    const current = buildScope3WasteData(wasteData.scope3WasteData);
    const next: Scope3WasteData = {
      ...current,
      ...updates,
      recyclingByMaterial: { ...current.recyclingByMaterial, ...(updates.recyclingByMaterial || {}) },
    };
    updateWasteData({ scope3WasteData: next });
  };

  useEffect(() => {
    if (!wasteData.scope3WasteData) return;
    const scope3Waste = buildScope3WasteData(wasteData.scope3WasteData);
    const toTonnes = (value: number | null, unit: "kg" | "tonnes"): number =>
      value === null || Number.isNaN(value) ? 0 : unit === "kg" ? value / 1000 : value;
    const recycling = scope3Waste.recyclingByMaterial ?? defaultRecyclingByMaterial;

    calculateWasteEmissions({
      landfillTonnes: toTonnes(scope3Waste.generalWasteToLandfill, scope3Waste.generalWasteUnit),
      recycledTonnes:
        toTonnes(recycling.paper, scope3Waste.recyclingUnit) +
        toTonnes(recycling.plastic, scope3Waste.recyclingUnit) +
        toTonnes(recycling.glass, scope3Waste.recyclingUnit) +
        toTonnes(recycling.metal, scope3Waste.recyclingUnit) +
        toTonnes(recycling.other, scope3Waste.recyclingUnit),
      compostingTonnes: toTonnes(recycling.organic, scope3Waste.recyclingUnit),
      hazardousTonnes: toTonnes(scope3Waste.hazardousWasteDisposal, scope3Waste.hazardousWasteUnit),
    }).then((result) => {
      if (result) {
        setWasteBreakdown(result.breakdown);
        const normalizedTotal = parseFloat(result.total.toFixed(6));
        if (scope3Waste.scope3WasteTco2e !== normalizedTotal) {
          updateWasteData({ scope3WasteData: { ...scope3Waste, scope3WasteTco2e: normalizedTotal } });
        }
      } else {
        setWasteBreakdown(null);
      }
    });
  }, [
    wasteData.scope3WasteData?.generalWasteToLandfill,
    wasteData.scope3WasteData?.generalWasteUnit,
    wasteData.scope3WasteData?.recyclingByMaterial,
    wasteData.scope3WasteData?.recyclingUnit,
    wasteData.scope3WasteData?.hazardousWasteDisposal,
    wasteData.scope3WasteData?.hazardousWasteUnit,
    updateWasteData,
  ]);

  const scope3Waste = buildScope3WasteData(wasteData.scope3WasteData);
  const recyclingFields = [
    ["paper", "Paper/Cardboard"],
    ["plastic", "Plastic"],
    ["metal", "Metal"],
    ["organic", "Organic/Compost"],
    ["glass", "Glass"],
    ["other", "Other Recycled"],
  ] as const;

  return (
    <div className="flex flex-col gap-6">
      <CardPanelHeader
        title={"Resource Use, Circular Economy & Waste (B7)"}
        description={"Track your circular economy approach and waste management"}
        onClose={onClose}
        onSave={forceSave}
        saveStatus={saveStatus}
        disabled={disabled}
        tag={"Environment"}
      />

      {disabled && <LockedBanner />}

      <SectionCard header={<SectionHeader icon={<CircularIcon />} title={"1) Circular Economy Principles"} />}>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium leading-5 text-slate-700">Indicate if and which circular economy principles you apply</Label>
              <InfoIcon />
            </div>
            <div className="grid grid-cols-1 gap-4 pt-2">
              {CIRCULAR_ECONOMY_PRINCIPLES.map((principle) => {
                const selectedPrinciples = wasteData.circularEconomyPrinciples ?? [];
                const checked = selectedPrinciples.includes(principle.value);
                return (
                  <PrincipleCheckbox
                    key={principle.value}
                    id={`circular_${principle.value.replace(/\s+/g, "_").toLowerCase()}`}
                    checked={checked}
                    disabled={disabled}
                    label={principle.value}
                    example={principle.example}
                    onToggle={() => {
                      const next = checked
                        ? selectedPrinciples.filter((p) => p !== principle.value)
                        : [...selectedPrinciples, principle.value];
                      updateWasteData({ circularEconomyPrinciples: next, appliesCircularEconomy: next.length > 0 });
                    }}
                  />
                );
              })}
            </div>
          </div>
          {(wasteData.circularEconomyPrinciples ?? []).length > 0 && (
            <div className="flex flex-col gap-2">
              <Label required htmlFor="circular_desc" className="text-sm font-medium leading-5 text-slate-700">Describe how you apply circular economy principles</Label>
              <Textarea id="circular_desc" placeholder="e.g. We reuse packaging materials, compost organic waste, and refurbish equipment rather than replacing it..." className={TEXTAREA_CLASS} value={wasteData.circularEconomyDescription} onChange={(e) => updateWasteData({ circularEconomyDescription: e.target.value })} disabled={disabled} required />
              <p className="text-sm leading-5 text-slate-500">Provide specific examples of circular economy initiatives in your operations.</p>
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard header={<SectionHeader icon={<TotalsIcon />} title={"2) Annual Waste Totals"} />}>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label required htmlFor="non_hazardous_waste" className="text-sm font-medium leading-5 text-slate-700">{"Non-hazardous waste generated"}</Label>
              <SplitNumberSelect inputId="non_hazardous_waste" value={wasteData.nonHazardousWaste} placeholder={"e.g. 500"} onValueChange={(value) => handleNumberChange("nonHazardousWaste", value)} unit={wasteData.nonHazardousWasteUnit} onUnitChange={(value) => updateWasteData({ nonHazardousWasteUnit: value })} disabled={disabled} />
              <p className="text-xs leading-4 text-slate-400">{"Required"}</p>
            </div>
            <div className="flex flex-col gap-2">
              <Label required htmlFor="hazardous_waste" className="text-sm font-medium leading-5 text-slate-700">{"Hazardous waste generated"}</Label>
              <SplitNumberSelect inputId="hazardous_waste" value={wasteData.hazardousWaste} placeholder={"e.g. 50"} onValueChange={(value) => handleNumberChange("hazardousWaste", value)} unit={wasteData.hazardousWasteUnit} onUnitChange={(value) => updateWasteData({ hazardousWasteUnit: value })} disabled={disabled} />
              <p className="text-xs leading-4 text-slate-400">{"Required"}</p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="waste_diverted" className="text-sm font-medium leading-5 text-slate-700">{"Waste diverted to recycling/reuse (optional)"}</Label>
            <SplitNumberSelect inputId="waste_diverted" value={wasteData.wasteDiverted} placeholder={"e.g. 250"} onValueChange={(value) => handleNumberChange("wasteDiverted", value)} unit={wasteData.wasteDivertedUnit} onUnitChange={(value) => updateWasteData({ wasteDivertedUnit: value })} disabled={disabled} />
          </div>
        </div>
        <InfoCallout label={`${"Tip:"}`} text={"Check waste manifests, recycling receipts, and disposal records"} />
      </SectionCard>

      <SectionCard header={<SectionHeader icon={<MaterialsIcon />} title={"3) Materials Mass-Flow (Optional)"} />}>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium leading-5 text-slate-700">{"Do you track material inputs (e.g., raw materials, components, packaging)?"}</p>
            <div className="grid grid-cols-1 gap-4 pt-2 md:grid-cols-2">
              <OptionCard id="materials_yes" name="materials_tracking" selected={wasteData.tracksMaterials === true} onSelect={() => updateWasteData({ tracksMaterials: true })} disabled={disabled} label={"Yes"} />
              <OptionCard id="materials_no" name="materials_tracking" selected={wasteData.tracksMaterials === false} onSelect={() => updateWasteData({ tracksMaterials: false })} disabled={disabled} label={"No"} />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="materials_desc" className="text-sm font-medium leading-5 text-slate-700">{"If Yes: List key material categories and annual mass used"}</Label>
            <Textarea id="materials_desc" placeholder={"e.g. Steel: 2000 kg, Plastic: 500 kg, Cardboard: 1500 kg, Wood: 800 kg..."} className={TEXTAREA_CLASS} value={wasteData.materialsDescription} onChange={(e) => updateWasteData({ materialsDescription: e.target.value })} disabled={disabled} />
            <p className="text-sm leading-5 text-slate-500">{"Format: Material name: quantity (unit) per line"}</p>
          </div>
        </div>
        <InfoCallout label={`${"Note:"}`} text={"Include only if your business is in a material-flow sector (manufacturing, construction, etc.)"} />
      </SectionCard>

      <section className={SCOPE_PANEL_CLASS}>
        <div className="flex flex-col gap-6">
          <div className="text-sm font-bold uppercase leading-5 tracking-wide text-gray-800">{"Waste Disposal Emissions"}</div>
          <div className="inline-flex w-fit rounded-[5px] bg-primary px-3 py-0.5 text-sm font-bold uppercase leading-5 tracking-wide text-white">Scope 3</div>
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <div className="flex flex-col gap-4">
              <div className="text-sm font-bold leading-5 text-gray-800">Waste diverted to recycling/reuse (If applicable)</div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {recyclingFields.map(([key, label]) => <div key={key} className="flex flex-col gap-2"><Label className="text-xs font-bold uppercase leading-4 tracking-wide text-gray-700">{label}</Label><Input type="number" placeholder="0" step="0.01" value={scope3Waste.recyclingByMaterial[key] ?? ""} onChange={(e) => { const val = e.target.value.trim() === "" ? null : parseFloat(e.target.value); updateScope3WasteData({ recyclingByMaterial: { [key]: val } }); }} disabled={disabled} className={STANDALONE_INPUT_CLASS} /></div>)}
              </div>
              <div className="max-w-[180px]">
                <Label className="text-xs font-bold uppercase leading-4 tracking-wide text-gray-700">Unit</Label>
                <Select value={scope3Waste.recyclingUnit} onValueChange={(value: "kg" | "tonnes") => updateScope3WasteData({ recyclingUnit: value })} disabled={disabled}>
                  <SelectTrigger className="mt-2 h-10 rounded-xl border-slate-200 bg-slate-50 text-sm text-slate-900 shadow-none focus:ring-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="kg">kg</SelectItem><SelectItem value="tonnes">tonnes</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="general_waste_landfill" className="text-xs font-bold uppercase leading-4 tracking-wide text-gray-700">{"General Waste to Landfill (tonnes)"}</Label>
                <div className="flex items-stretch"><Input id="general_waste_landfill" type="number" placeholder={"e.g. 5.2"} step="0.01" value={scope3Waste.generalWasteToLandfill ?? ""} onChange={(e) => { const val = e.target.value.trim() === "" ? null : parseFloat(e.target.value); updateScope3WasteData({ generalWasteToLandfill: val }); }} disabled={disabled} className={INPUT_CLASS} /><Select value={scope3Waste.generalWasteUnit} onValueChange={(value: "kg" | "tonnes") => updateScope3WasteData({ generalWasteUnit: value })} disabled={disabled}><SelectTrigger className="h-11 w-[120px] rounded-l-none rounded-r-xl border-slate-300 bg-slate-50 text-xs text-slate-500 shadow-none focus:ring-slate-300"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="kg">kg</SelectItem><SelectItem value="tonnes">tonnes</SelectItem></SelectContent></Select></div>
                <p className="text-xs leading-4 text-slate-400">{"Mixed waste sent to landfill"}</p>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="hazardous_waste_disposal" className="text-xs font-bold uppercase leading-4 tracking-wide text-gray-700">{"Hazardous Waste Disposal (tonnes)"}</Label>
                <div className="flex items-stretch"><Input id="hazardous_waste_disposal" type="number" placeholder={"e.g. 0.5"} step="0.01" value={scope3Waste.hazardousWasteDisposal ?? ""} onChange={(e) => { const val = e.target.value.trim() === "" ? null : parseFloat(e.target.value); updateScope3WasteData({ hazardousWasteDisposal: val }); }} disabled={disabled} className={INPUT_CLASS} /><Select value={scope3Waste.hazardousWasteUnit} onValueChange={(value: "kg" | "tonnes") => updateScope3WasteData({ hazardousWasteUnit: value })} disabled={disabled}><SelectTrigger className="h-11 w-[120px] rounded-l-none rounded-r-xl border-slate-300 bg-slate-50 text-xs text-slate-500 shadow-none focus:ring-slate-300"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="kg">kg</SelectItem><SelectItem value="tonnes">tonnes</SelectItem></SelectContent></Select></div>
                <p className="text-xs leading-4 text-slate-400">{"Specialized waste disposal"}</p>
              </div>
            </div>
          </div>
          <InfoCallout label={`${"Note:"}`} text={"Scope 3 waste emissions will be calculated automatically based on disposal methods and waste quantities entered above."} />
          {wasteData.scope3WasteData && <>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <Scope3Metric label="Recycling &amp; composting" value={(wasteBreakdown?.recycledTco2e ?? 0).toFixed(3)} />
              <Scope3Metric label="General waste — landfill" value={(wasteBreakdown?.landfillTco2e ?? 0).toFixed(3)} />
              <Scope3Metric label="Hazardous disposal" value={(wasteBreakdown?.hazardousTco2e ?? 0).toFixed(3)} />
            </div>
            <div className="border-t border-slate-300 pt-4"><div className="flex flex-col gap-2 border-t border-slate-200 px-1 pt-4 sm:flex-row sm:items-center sm:justify-between"><span className="text-lg font-bold leading-7 text-gray-900">{"Total Scope 3 Waste"}</span><span className="text-3xl font-bold leading-9 text-gray-900">{(wasteData.scope3WasteData?.scope3WasteTco2e ?? 0).toFixed(3)} tCO2e</span></div></div>
          </>}
        </div>
      </section>

      <SectionCard header={<SectionHeader icon={<NotesIcon />} title={"4) Additional Notes (Optional)"} />}>
        <div className="flex flex-col gap-2">
          <Label htmlFor="waste_notes" className="text-sm font-medium leading-5 text-slate-700">{"Notes about waste management"}</Label>
          <Textarea id="waste_notes" placeholder={"e.g. Waste reduction initiatives, partnerships with recyclers, composting programs..."} className={TEXTAREA_CLASS} value={wasteData.notes} onChange={(e) => updateWasteData({ notes: e.target.value })} disabled={disabled} />
        </div>
      </SectionCard>

      <SectionCard header={<SectionHeader icon={<UploadSectionIcon />} title={"Supporting Documents"} />}>
        <DocumentUploadSection fieldIdentifier="environment-waste" documents={wasteSupportingDocuments} onDocumentsChange={setWasteSupportingDocuments} currentTab="environment" label={"Supporting Documents"} description={"Upload waste manifests, recycling receipts, or material sourcing records"} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls,.csv" maxFiles={10} borderColor="border-slate-200" hideHeader className="rounded-[24px] border-2 border-slate-200 bg-slate-50 p-4 sm:p-10" emptyStateButtonClassName="rounded-[24px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300" documentsListClassName="space-y-3" itemClassName="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between" actionButtonClassName="rounded-3xl border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700" emptyStateContent={<div className="flex flex-col items-center text-center"><div className="pb-4"><div className="rounded-full bg-slate-500/5 p-4"><UploadCloudIcon /></div></div><p className="text-base font-bold leading-6 text-slate-800">Click to upload or drag and drop</p><p className="pt-1 text-sm leading-5 text-slate-500">{"Upload waste manifests, recycling receipts, or material sourcing records"}</p></div>} />
      </SectionCard>

      <TransitionMeasuresBlock topicId="waste" currentTab="environment" disabled={disabled} />

      <ProgressCard completion={wasteCompletion} />
    </div>
  );
}

